import { useState } from "react";

/**
 * Curated "Selected work" strip — three hero pieces (1 video · 1 article · 1 photo).
 * Sits between Hero and About so editors see the best work in 5 seconds.
 */
export default function Selected({ video, article, photo }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="selected"
      data-testid="selected-section"
      className="relative bg-[#0A0A0A] text-[#EAEAEA] py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-px bg-[#C4A27A]" />
            <span className="font-mono-overline text-[#C4A27A]">Selected work — recent</span>
          </div>
          <div className="font-mono-overline text-[#EAEAEA]/40 text-[10px]">
            One read · one watch · one frame
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {/* Featured video */}
          {video && (
            <div className="md:col-span-7" data-testid="selected-video">
              <div className="yt-aspect img-zoom border border-white/10">
                {playing ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      className="yt-thumb"
                      src={video.custom_thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                      alt={video.title}
                    />
                    <button
                      data-testid="selected-video-play"
                      className="thumb-play"
                      onClick={() => setPlaying(true)}
                      aria-label={`Play ${video.title}`}
                    >
                      <div className="play-circle">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </button>
                  </>
                )}
              </div>
              <div className="mt-3">
                <div className="font-mono-overline text-[#C4A27A] mb-1">Watch · {video.outlet}</div>
                <h3 className="font-serif-display text-lg md:text-xl text-[#EAEAEA] leading-tight">
                  {video.title}
                </h3>
              </div>
            </div>
          )}

          {/* Right column: article + photo stacked */}
          <div className="md:col-span-5 grid grid-cols-1 gap-5 md:gap-6">
            {article && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="selected-article"
                className="group block"
              >
                <div className="img-zoom aspect-[16/9] border border-white/10">
                  <img
                    src={`${BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(article.image_url)}`}
                    alt={article.headline}
                    loading="lazy"
                  />
                </div>
                <div className="mt-3">
                  <div className="font-mono-overline text-[#C4A27A] mb-1">Read · {article.outlet}</div>
                  <h3 className="font-serif-display text-lg md:text-xl text-[#EAEAEA] leading-tight group-hover:text-[#C4A27A] transition-colors">
                    {article.headline}
                  </h3>
                </div>
              </a>
            )}

            {photo && (
              <div data-testid="selected-photo">
                <div className="img-zoom aspect-[16/9] border border-white/10">
                  <img
                    src={photo.image_url.startsWith("https://i.cbc.ca/") ? `${BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(photo.image_url)}` : photo.image_url}
                    alt={photo.caption}
                    loading="lazy"
                  />
                </div>
                <div className="mt-3">
                  <div className="font-mono-overline text-[#C4A27A] mb-1">Frame · {photo.location}</div>
                  <h3 className="font-serif-display text-lg md:text-xl text-[#EAEAEA] leading-tight italic">
                    {photo.caption}
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
